import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckboxDropdown from "../../components/form/FormCheckboxDropdown";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import ModalTabContent from "../../components/modal/ModalTabContent";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import { referenceOperations } from "../../modules/definition/references";
import preprocessorQuery from "../../utils/preprocessorQuery";
import selectors from "./selectors";
import { wrapComponents } from "./utils";

const renderReferences = useProps => {
  const eventTypes = selectors.eventTypesFromAllTypes(useProps.types);
  const personTypes = selectors.personTypesFromAllTypes(useProps.types);
  const locationTypes = selectors.locationTypesFromAllTypes(useProps.types);
  const otherTypes = selectors.otherTypesFromAllTypes(useProps.types);

  const eventToEventReferences = selectors
    .eventToEventReferences(useProps.references, eventTypes)
    .filter(r => r.id !== REFERENCE_RELATES_TO);
  const eventToPersonReferences = selectors
    .eventToPersonReferences(useProps.references, eventTypes, personTypes)
    .filter(r => r.id !== REFERENCE_RELATES_TO && !eventToEventReferences.find(ref => ref.id === r.id));
  const eventToLocationsReferences = selectors
    .eventToPersonReferences(useProps.references, eventTypes, locationTypes)
    .filter(
      r =>
        r.id !== REFERENCE_RELATES_TO &&
        !eventToEventReferences.merge(eventToPersonReferences).find(ref => ref.id === r.id)
    );
  const personToPersonReferences = selectors.personToPersonReferences(useProps.references, personTypes).filter(
    r =>
      r.id !== REFERENCE_RELATES_TO &&
      !eventToEventReferences
        .merge(eventToPersonReferences)
        .merge(eventToLocationsReferences)
        .find(ref => ref.id === r.id)
  );
  const otherReferences = selectors.personToPersonReferences(useProps.references, otherTypes).filter(
    r =>
      r.id !== REFERENCE_RELATES_TO &&
      !eventToEventReferences
        .merge(eventToPersonReferences)
        .merge(eventToLocationsReferences)
        .merge(personToPersonReferences)
        .find(ref => ref.id === r.id)
  );

  const getOptionList = options =>
    options
      .map(o => {
        return {
          label: o.label,
          value: o.id,
          selected: o.enabled
        };
      })
      .toList();

  const onChange = (id, enabled) => useProps.dispatchLocal(referenceOperations.changeEnabled(id, enabled));

  const children = [
    <ModalRow key={"eventToEventReferences"}>
      <ModalField label={"Events relate to Events as"}>
        <FormCheckboxDropdown options={getOptionList(eventToEventReferences)} onChange={onChange} />
      </ModalField>
    </ModalRow>,
    <ModalRow key={"eventToPersonReferences"}>
      <ModalField label={"Events relate to People as"}>
        <FormCheckboxDropdown options={getOptionList(eventToPersonReferences)} onChange={onChange} />
      </ModalField>
    </ModalRow>,
    <ModalRow key={"eventToLocationReferences"}>
      <ModalField label={"Events relate to Locations as"}>
        <FormCheckboxDropdown options={getOptionList(eventToLocationsReferences)} onChange={onChange} />
      </ModalField>
    </ModalRow>,
    <ModalRow key={"personToPersonReferences"}>
      <ModalField label={"People relate to People as"}>
        <FormCheckboxDropdown options={getOptionList(personToPersonReferences)} onChange={onChange} />
      </ModalField>
    </ModalRow>,
    <ModalRow key={"otherReferences"}>
      <ModalField label={"Other"}>
        <FormCheckboxDropdown options={getOptionList(otherReferences)} onChange={onChange} />
      </ModalField>
    </ModalRow>
  ];

  return wrapComponents(children, "Relationships", true, () => useProps.onAdvancedSettingsClick(1), "Edit");
};

class SettingsReferenceGroup extends PureComponent {
  constructor(props, context) {
    super(props, context);
  }

  render() {
    const isIos = preprocessorQuery.isIos();

    return (
      <ModalTabContent scrollable={true}>
        {renderReferences(this.props)}
        {isIos ? (
          <ModalGroup label={isIos ? "Advanced" : null}>
            <ModalRow alignRight={true}>
              <ModalField flex={1}>
                <FormButton
                  linkStyle={!isIos}
                  label="Advanced Settings"
                  onClick={() => this.props.onAdvancedSettingsClick(1)}
                  rightAlign={true}
                />
              </ModalField>
            </ModalRow>
          </ModalGroup>
        ) : null}
      </ModalTabContent>
    );
  }
}

SettingsReferenceGroup.propTypes = {
  references: ImmutablePropTypes.list.isRequired,
  dispatchLocal: PropTypes.func.isRequired,
  onAdvancedSettingsClick: PropTypes.func.isRequired
};

const mapDispatchToProps = (_dispatch, _props) => {
  // Note: We are always using dispatchLocal here so that the action only travels
  // back to the Settings Modal.
  // Changes are committed to the root store only when the dialog is confirmed.
  return {};
};

export default connect(null, mapDispatchToProps)(SettingsReferenceGroup);
